import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { cn } from '@/lib/utils';
import { Text, TextClassContext } from '@/components/ui/text';

const buttonVariants = cva(
  'group flex items-center justify-center rounded-xl web:ring-offset-background web:transition-colors web:focus-visible:outline-none web:focus-visible:ring-2 web:focus-visible:ring-ring web:focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        primary:
          'bg-accent text-accent-foreground web:hover:opacity-90 active:opacity-90',
        secondary:
          'border border-border bg-secondary text-foreground web:hover:bg-accent/10 active:bg-accent/10',
        destructive:
          'bg-negative/20 text-negative web:hover:opacity-90 active:opacity-90',
        ghost:
          'web:hover:bg-accent/10 web:hover:text-accent-foreground active:bg-accent/10',
        link: 'web:underline-offset-4 web:hover:underline web:focus:underline',
        subtle: 'bg-surface-elevated web:hover:opacity-90 active:opacity-90',
        surface: 'bg-surface web:hover:opacity-90 active:opacity-90',
      },
      size: {
        default: 'px-6 py-3.5 native:px-6 native:py-3.5',
        sm: 'h-9 rounded-lg px-4 native:h-10 native:px-4 native:py-2',
        lg: 'h-12 rounded-xl px-8 native:h-14 native:px-8 native:py-4',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
);

const buttonTextVariants = cva(
  'web:whitespace-nowrap text-sm native:text-base font-semibold web:transition-colors',
  {
    variants: {
      variant: {
        primary: 'text-accent-foreground',
        secondary: 'text-foreground',
        destructive: 'text-negative',
        ghost: 'group-active:text-accent-foreground',
        link: 'text-primary group-active:underline',
        subtle: 'text-text-secondary',
        surface: 'text-text-secondary',
      },
      size: {
        default: '',
        sm: 'native:text-sm',
        lg: 'native:text-base',
        icon: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
);

type ButtonProps = React.ComponentPropsWithoutRef<typeof Pressable> &
  VariantProps<typeof buttonVariants> & {
    loading?: boolean;
    textClassName?: string;
    hitSlop?:
      | number
      | { top?: number; bottom?: number; left?: number; right?: number };
  };

const Button = React.forwardRef<
  React.ElementRef<typeof Pressable>,
  ButtonProps
>(
  (
    {
      className,
      variant,
      size,
      disabled: propDisabled,
      children,
      loading,
      textClassName,
      hitSlop,
      ...props
    },
    ref,
  ) => {
    const disabled = propDisabled || loading;
    const defaultHitSlop = size === 'icon' ? 8 : undefined;
    return (
      <TextClassContext.Provider
        value={buttonTextVariants({
          variant,
          size,
          className: cn('web:pointer-events-none', textClassName),
        })}
      >
        <Pressable
          {...props}
          className={cn(
            disabled && 'opacity-50 web:pointer-events-none',
            buttonVariants({ variant, size, className }),
          )}
          disabled={disabled}
          ref={ref}
          role="button"
          hitSlop={hitSlop ?? defaultHitSlop}
        >
          {loading ? (
            <ActivityIndicator size="small" color="white" />
          ) : typeof children === 'string' ? (
            <Text>{children}</Text>
          ) : (
            children
          )}
        </Pressable>
      </TextClassContext.Provider>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonTextVariants, buttonVariants };
export type { ButtonProps };
